import { Component } from "@theme/component";
import { debounce, isClickedOutside, onAnimationEnd } from "@theme/utilities";

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ["dialog"];

  /** Scroll position saved when the body was locked. */
  #previousScrollY = 0;

  /** Whether this component currently holds the body scroll lock. */
  #bodyLocked = false;

  /** Guards against double-opening while the open frame is pending. */
  #opening = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener("resize", this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener("resize", this.#handleResize);
    }

    // If the component is removed while open, never leave the page locked
    this.#removeInteractionListeners();
    this.#unlockBody();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Locks page scroll by fixing the body in place.
   * @param {number} scrollY - The scroll position to preserve.
   */
  #lockBody(scrollY) {
    if (this.#bodyLocked) return;

    this.#previousScrollY = scrollY;
    this.#bodyLocked = true;

    document.body.style.width = "100%";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
  }

  /**
   * Releases the body scroll lock, if this component holds it.
   * Safe to call multiple times.
   */
  #unlockBody() {
    if (!this.#bodyLocked) return;

    this.#bodyLocked = false;

    document.body.style.width = "";
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo({ top: this.#previousScrollY, behavior: "instant" });
  }

  #addInteractionListeners() {
    this.addEventListener("click", this.#handleClick);
    this.addEventListener("keydown", this.#handleKeyDown);
  }

  #removeInteractionListeners() {
    this.removeEventListener("click", this.#handleClick);
    this.removeEventListener("keydown", this.#handleKeyDown);
  }

  /**
   * Runs when the dialog closes by any path other than closeDialog()
   * (native close, form[method=dialog], dialog.close() from other code).
   * closeDialog() unlocks first, so this is a no-op in the normal flow.
   */
  #handleNativeClose = () => {
    if (!this.#bodyLocked) return;

    this.#removeInteractionListeners();
    this.refs.dialog?.classList.remove("dialog-closing");
    this.#unlockBody();
    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#opening) return;

    this.#opening = true;
    const scrollY = window.scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      this.#opening = false;
      if (dialog.open || !dialog.isConnected) return;

      this.#lockBody(scrollY);

      dialog.showModal();
      dialog.addEventListener("close", this.#handleNativeClose, { once: true });
      this.dispatchEvent(new DialogOpenEvent());

      this.#addInteractionListeners();
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    this.#removeInteractionListeners();

    if (!dialog?.open) {
      // Dialog already gone/closed (e.g. re-rendered) — still release the page
      this.#unlockBody();
      return;
    }

    // Force browser to restart animation by resetting it
    dialog.style.animation = "none";

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add("dialog-closing");
    dialog.style.animation = "";

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // Unlock before close() so #handleNativeClose sees nothing to do
    this.#unlockBody();

    dialog.removeEventListener("close", this.#handleNativeClose);
    dialog.close();
    dialog.classList.remove("dialog-closing");

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== "Escape") return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute("dialog-active-min-width"));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute("dialog-active-max-width"));
  }
}

if (!customElements.get("dialog-component"))
  customElements.define("dialog-component", DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = "dialog:open";
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = "dialog:close";
}

document.addEventListener(
  "toggle",
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute("scroll-lock")) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute("scroll-lock", "");
        } else {
          document.documentElement.removeAttribute("scroll-lock");
        }
      }
    }
  },
  { capture: true },
);
